import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    ArrowRight2,
    Global,
    InfoCircle,
    PercentageCircle,
    ReceiptDiscount,
} from "iconsax-reactjs";
import { InputNumberFormat, format, unformat } from "@react-input/number-format";
import OnboardingLayout from "component/auth/OnboardingLayout";
import SelectNetworkModal from "component/wallet/SelectNetworkModal";
import CryptoConversionConfirmModal from "component/wallet/CryptoConversionConfirmModal";
import InfoTooltip from "component/InfoTooltip";
import CurrencyToggle from "component/currencyToggle";
import { CurrencySelector } from "component/wallet/CurrencySelector";
import PinModal from "component/pay/PinModal";
import TransferSuccessModal from "component/pay/TransferSuccessModal";
import { fetchCoinNetworks, fetchCryptoQuote, fetchTransferFees } from "api/fetchers";
import { formatMoney } from "utilities/formatMoney";

const LOCALE = "en";

const normalizeStandard = (tokenStandard = "") => {
    const raw = String(tokenStandard || "").toUpperCase();
    if (!raw) return "";
    if (raw === "TRC20") return "TRC-20";
    if (raw === "ERC20") return "ERC-20";
    return raw;
};

const toTitle = (value = "") =>
    String(value || "")
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
        .join(" ");

const getNetworkLabel = (network) => {
    const name = network?.name || network?.network || network?.asset?.name || network?.symbol || "";
    const standard =
        normalizeStandard(network?.label || network?.shortName || network?.asset?.tokenStandard) ||
        "";

    return `${toTitle(name || "Network")}${standard ? ` (${standard})` : ""}`;
};

const getNetworkKey = (network) =>
    String(network?.id || network?.network || network?.name || network?.symbol || "").toLowerCase();

function CryptoConverter() {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        business,
        wallets,
        currency: currentCurrency,
        cryptoTransfer,
    } = useSelector((state) => state);

    const [amount, setAmount] = useState("");
    const [sourceNetwork, setSourceNetwork] = useState(null);
    const [destinationNetwork, setDestinationNetwork] = useState(null);
    const [isNetworkModalOpen, setIsNetworkModalOpen] = useState(false);
    const [activeNetworkPicker, setActiveNetworkPicker] = useState(null);
    const [networks, setNetworks] = useState([]);
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [quoteError, setQuoteError] = useState("");
    const [minAmount, setMinAmount] = useState(0);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [showPin, setShowPin] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const numericAmount = Number.parseFloat(amount || "0") || 0;
    const mode =
        new URLSearchParams(location.search).get("mode") === "currency" ? "currency" : "network";
    const currency = String(
        ["USDC", "USDT"].includes(String(currentCurrency || "").toUpperCase())
            ? currentCurrency
            : cryptoTransfer?.token || "USDC"
    ).toUpperCase();
    const [targetCurrency, setTargetCurrency] = useState(currency === "USDC" ? "USDT" : "USDC");
    const businessId = business?._id;

    const wallet = useMemo(
        () => wallets.find((item) => String(item?.currency || "").toUpperCase() === currency),
        [wallets, currency]
    );

    useEffect(() => {
        if (mode !== "currency") {
            setTargetCurrency(currency);
            return;
        }

        if (String(targetCurrency || "").toUpperCase() === currency) {
            setTargetCurrency(currency === "USDC" ? "USDT" : "USDC");
        }
    }, [mode, currency, targetCurrency]);

    useEffect(() => {
        if (!currency || !businessId) {
            setNetworks([]);
            return;
        }

        let cancelled = false;
        (async () => {
            const list = await fetchCoinNetworks({
                currency: String(currency).toUpperCase(),
                businessId,
            });
            if (!cancelled) setNetworks(Array.isArray(list) ? list : []);
        })();

        return () => {
            cancelled = true;
        };
    }, [currency, businessId]);

    useEffect(() => {
        if (!businessId || !currency) return;

        let cancelled = false;
        (async () => {
            try {
                const res = await fetchTransferFees({
                    businessId,
                    params: {
                        currency: String(currency).toUpperCase(),
                        currencyTo: String(
                            mode === "currency" ? targetCurrency : currency
                        ).toUpperCase(),
                        amount: numericAmount || 0,
                    },
                });

                const data = res?.data ?? res ?? {};
                if (!cancelled) setMinAmount(Number(data.minimumTransferAmount) || 0);
            } catch {
                if (!cancelled) setMinAmount(0);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [businessId, currency, targetCurrency, mode, numericAmount]);

    useEffect(() => {
        if (!networks.length) {
            setSourceNetwork(null);
            setDestinationNetwork(null);
            return;
        }

        setSourceNetwork((current) => {
            if (
                current &&
                networks.some((network) => getNetworkKey(network) === getNetworkKey(current))
            ) {
                return current;
            }
            return null;
        });

        setDestinationNetwork((current) => {
            if (
                current &&
                networks.some((network) => getNetworkKey(network) === getNetworkKey(current))
            ) {
                return current;
            }
            return null;
        });
    }, [networks]);

    const formattedAmount = useMemo(() => {
        return amount ? format(amount, { locales: LOCALE, maximumFractionDigits: 2 }) : "";
    }, [amount]);

    const totalBalance = Number(wallet?.balance || 0);
    const selectedSourceProvider = useMemo(
        () =>
            networks.find((network) => getNetworkKey(network) === getNetworkKey(sourceNetwork)) ||
            null,
        [networks, sourceNetwork]
    );
    const availableBalance =
        selectedSourceProvider !== null
            ? Number(selectedSourceProvider?.balance || 0)
            : totalBalance;
    const isSameNetwork =
        Boolean(sourceNetwork && destinationNetwork) &&
        getNetworkKey(sourceNetwork) === getNetworkKey(destinationNetwork);
    const insufficientFunds = numericAmount > availableBalance;
    const tooLow = numericAmount > 0 && numericAmount < (minAmount || 0);

    useEffect(() => {
        if (
            !businessId ||
            !currency ||
            !sourceNetwork ||
            !destinationNetwork ||
            !numericAmount ||
            isSameNetwork
        ) {
            setQuote(null);
            setQuoteError("");
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                setQuoteLoading(true);
                setQuoteError("");
                const data = await fetchCryptoQuote({
                    fromCurrency: currency,
                    toCurrency: mode === "currency" ? targetCurrency : currency,
                    fromNetwork:
                        sourceNetwork?.network || sourceNetwork?.id || sourceNetwork?.name || "",
                    toNetwork:
                        destinationNetwork?.network ||
                        destinationNetwork?.id ||
                        destinationNetwork?.name ||
                        "",
                    amount: numericAmount,
                    businessId,
                });
                if (!cancelled) setQuote(data || {});
            } catch (error) {
                if (!cancelled) {
                    setQuote(null);
                    setQuoteError(error?.response?.data?.message || "Failed to fetch quote");
                }
            } finally {
                if (!cancelled) setQuoteLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        businessId,
        currency,
        targetCurrency,
        mode,
        sourceNetwork,
        destinationNetwork,
        numericAmount,
        isSameNetwork,
    ]);

    const quoteAmount = Number(quote?.amount || 0);
    const quoteFee = Number(quote?.networkFee || 0);
    const quoteRate = Number(quote?.rate || 0);
    const convertedAmount = quoteAmount > 0 ? quoteAmount : 0;
    const toCurrency = mode === "currency" ? targetCurrency : currency;
    const canSubmit =
        numericAmount > 0 &&
        !insufficientFunds &&
        !tooLow &&
        sourceNetwork &&
        destinationNetwork &&
        !quoteError &&
        !isSameNetwork;

    async function submitConversion({ pin }) {
        const payload = {
            fromNetwork: sourceNetwork?.network || sourceNetwork?.id || sourceNetwork?.name || "",
            toCurrency,
            toNetwork:
                destinationNetwork?.network ||
                destinationNetwork?.id ||
                destinationNetwork?.name ||
                "",
            fromCurrency: currency,
            amount: String(numericAmount || 0),
            naration: "",
            pin,
        };

        const headers = businessId ? { "x-business-id": businessId } : undefined;
        const res = await axios.post("/v1/wallets/swap-crypto", payload, { headers });
        return res?.data;
    }

    return (
        <OnboardingLayout
            title={
                mode === "currency"
                    ? "Convert to another crypto currency"
                    : "Convert to another network"
            }
            showSkip={false}
        >
            <div className="max-w-2xl mx-auto mt-10 md:px-4 space-y-10 pb-32 md:pb-0">
                <div className="w-full">
                    <h2 className="text-2xl font-normal mb-6">Enter amount</h2>

                    <div className="bg-white rounded-xl p-6 max-w-3xl w-full">
                        <div className="border-b border-[#E1E3F4] pb-4 mb-4">
                            <p className="mb-6 text-[14px] font-[450] text-[#383B56]">
                                You convert
                            </p>

                            <div className="mb-5 flex items-start justify-between gap-4">
                                <InputNumberFormat
                                    value={formattedAmount}
                                    onChange={(e) => {
                                        const raw = unformat(e.target.value || "", LOCALE);
                                        setAmount(raw);
                                    }}
                                    locales={LOCALE}
                                    maximumFractionDigits={2}
                                    type="text"
                                    inputMode="decimal"
                                    className={`w-full !border-0 caret-[#6F00FF] !p-0 !shadow-none text-7xl font-thin leading-none text-[#161827] outline-none ${
                                        insufficientFunds ? "border-b border-red-500" : ""
                                    }`}
                                    placeholder="0"
                                />

                                <div className="relative min-w-[110px] flex justify-end mt-1">
                                    <CurrencyToggle
                                        className="w-full text-sm bg-transparent"
                                        dropDownClassName="shadow-dropdown2 right-0 !top-[109%]"
                                        fetchWallet
                                        businessId={businessId}
                                        hideLabel
                                        includeCurrencies={["USDC", "USDT"]}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 text-sm md:flex-row md:items-start md:justify-between">
                                <div className="text-[16px] text-[#667085] md:text-right">
                                    {tooLow ? (
                                        <span className="text-[#667085]">
                                            Minimum amount is {formatMoney(minAmount)} {currency}
                                        </span>
                                    ) : insufficientFunds ? (
                                        <span className="text-red-600" role="alert">
                                            Insufficient balance. Available:{" "}
                                            {formatMoney(availableBalance)} {currency}
                                        </span>
                                    ) : (
                                        <span>
                                            Minimum amount is {formatMoney(minAmount)} {currency}
                                        </span>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 text-sm text-[#6B7280] md:text-right">
                                    <span>
                                        Balance: {formatMoney(availableBalance)} {currency}
                                    </span>
                                    <InfoTooltip
                                        message={
                                            sourceNetwork
                                                ? `This is your ${currency} balance on ${sourceNetwork?.id || sourceNetwork?.name}`
                                                : `This is your total ${currency} balance`
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="relative my-6">
                            <div className="absolute left-0 right-0 top-0 h-px bg-[#E1E3F4]" />
                            <div className="absolute left-0 right-0 bottom-0 h-px bg-[#E1E3F4]" />
                            <div className="absolute left-4 top-0 bottom-0 w-px bg-[#E1E3F4]" />

                            <div className="relative z-10 py-4 space-y-4">
                                <div className="flex items-start gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                        <Global size={16} color="#6F739B" variant="Bulk" />
                                    </div>
                                    <div className="flex mt-1.5 w-full items-center justify-between text-sm text-[#6F6B7B]">
                                        <span>Send from</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setActiveNetworkPicker("source");
                                                setIsNetworkModalOpen(true);
                                            }}
                                            className="text-[#7A27FF] font-[450] text-[16px] flex items-center gap-1"
                                        >
                                            {sourceNetwork
                                                ? getNetworkLabel(sourceNetwork)
                                                : "Select network"}
                                            <ArrowRight2 size={18} color="#6F00FF" />
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                        <ReceiptDiscount size={16} color="#6F739B" variant="Bulk" />
                                    </div>
                                    <div className="flex mt-1.5 w-full items-center justify-between text-sm text-[#6F6B7B]">
                                        <span>Fee</span>
                                        <span className="text-[#161827]">
                                            {quoteLoading
                                                ? "Fetching..."
                                                : quoteError
                                                  ? "~"
                                                  : `${formatMoney(quoteFee)} ${currency}`}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                        <PercentageCircle
                                            size={16}
                                            color="#6F739B"
                                            variant="Bulk"
                                        />
                                    </div>
                                    <div className="flex mt-1.5 w-full items-center justify-between text-sm text-[#6F6B7B]">
                                        <span>Rate</span>
                                        <span className="text-[#161827]">
                                            {quoteLoading
                                                ? "Fetching..."
                                                : quoteError
                                                  ? "Unavailable"
                                                  : `1 ${currency} = ${quoteRate || 0} ${toCurrency}`}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-4">
                            <div className="mb-4 flex items-center justify-between">
                                <span className="text-sm text-gray-500">You get</span>
                                {mode === "currency" ? (
                                    <CurrencySelector
                                        wallets={wallets.filter((w) =>
                                            ["USDC", "USDT"].includes(
                                                String(w?.currency || "").toUpperCase()
                                            )
                                        )}
                                        selected={targetCurrency}
                                        onSelect={(code) => setTargetCurrency(code)}
                                        exclude={[currency]}
                                        currencies={[{ code: "USDC" }, { code: "USDT" }]}
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveNetworkPicker("destination");
                                            setIsNetworkModalOpen(true);
                                        }}
                                        className="text-[#7A27FF] font-[450] text-[16px] flex items-center gap-1"
                                    >
                                        {destinationNetwork
                                            ? getNetworkLabel(destinationNetwork)
                                            : "Select network"}
                                        <ArrowRight2 size={18} color="#6F00FF" />
                                    </button>
                                )}
                            </div>

                            <div className="flex items-end gap-3">
                                <span className="text-7xl font-thin leading-none text-[#161827]">
                                    {formatMoney(convertedAmount)}
                                </span>
                            </div>

                            {mode === "currency" && (
                                <div className="mt-6 flex items-center justify-between">
                                    <span className="text-[16px] text-[#667085]">
                                        Send to{" "}
                                        {destinationNetwork
                                            ? getNetworkLabel(destinationNetwork)
                                            : "--"}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveNetworkPicker("destination");
                                            setIsNetworkModalOpen(true);
                                        }}
                                        className="text-[#7A27FF] font-[450] text-[16px] flex items-center gap-1"
                                    >
                                        {destinationNetwork
                                            ? getNetworkLabel(destinationNetwork)
                                            : "Select network"}
                                        <ArrowRight2 size={18} color="#6F00FF" />
                                    </button>
                                </div>
                            )}

                            {quoteError && (
                                <p className="mt-3 text-sm text-red-600" role="alert">
                                    {quoteError}
                                </p>
                            )}

                            {isSameNetwork && (
                                <p className="mt-3 text-sm text-red-600" role="alert">
                                    Choose a different destination network.
                                </p>
                            )}

                            {tooLow && (
                                <p className="mt-3 text-sm text-red-600" role="alert">
                                    Minimum amount is {formatMoney(minAmount)} {currency}.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="mt-6 flex w-full items-center justify-between">
                        <span className="flex items-center gap-2 text-sm text-[#4B4E68]">
                            <InfoCircle size={22} color="#667085" />
                            Instantly ⚡
                        </span>

                        <div className="flex items-center gap-4">
                            <button
                                type="button"
                                className="px-3 text-[18px] font-medium text-[#161827]"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                disabled={!canSubmit}
                                className={`h-14 min-w-[180px] rounded-2xl px-8 text-[18px] font-semibold ${
                                    canSubmit
                                        ? "bg-[#6F00FF] text-white"
                                        : "cursor-not-allowed bg-[#E9D7FE] text-white"
                                }`}
                                onClick={() => setIsConfirmOpen(true)}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <SelectNetworkModal
                isOpen={isNetworkModalOpen}
                onClose={() => {
                    setIsNetworkModalOpen(false);
                    setActiveNetworkPicker(null);
                }}
                onSelect={(network) => {
                    if (activeNetworkPicker === "source") {
                        setSourceNetwork(network);
                    } else {
                        setDestinationNetwork(network);
                    }
                    setIsNetworkModalOpen(false);
                    setActiveNetworkPicker(null);
                }}
                selectedId={
                    activeNetworkPicker === "source" ? sourceNetwork?.id : destinationNetwork?.id
                }
                currency={currency}
                businessId={businessId}
            />

            <CryptoConversionConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={() => {
                    setIsConfirmOpen(false);
                    setShowPin(true);
                }}
                amount={formatMoney(numericAmount)}
                convertedAmount={formatMoney(convertedAmount)}
                fee={formatMoney(quoteFee)}
                fromCurrency={currency}
                toCurrency={toCurrency}
                fromNetwork={sourceNetwork ? getNetworkLabel(sourceNetwork) : ""}
                toNetwork={destinationNetwork ? getNetworkLabel(destinationNetwork) : ""}
            />

            <PinModal
                isOpen={showPin}
                onClose={() => setShowPin(false)}
                onConfirm={async (pin) => {
                    try {
                        await submitConversion({ pin });
                        setShowPin(false);
                        setShowSuccess(true);
                    } catch (error) {
                        throw error;
                    }
                }}
                header="Enter payment PIN"
            />

            <TransferSuccessModal
                isOpen={showSuccess}
                onClose={() => navigate("/wallets")}
                onDone={() => navigate("/wallets")}
            />
        </OnboardingLayout>
    );
}

export default CryptoConverter;
